import React, { useMemo, useState } from 'react';
import {
  LayoutDashboard,
  X,
} from 'lucide-react';
import type {
  BonusPlan,
  CockpitSettings,
  DailyFinanceReport,
  ObligationSettings,
  OwnerDrawKind,
  OverheadExpense,
  PayrollPosition,
  TaxPlan,
} from '../../types/finances';
import {
  OWNER_DRAW_KIND_LABELS,
  OWNER_DRAW_KINDS,
  PAYROLL_DEPARTMENT_LABELS,
} from '../../types/finances';
import type { CashFlowProjection } from '../../utils/cashFlowProjection';
import type { ComputedTaxPlan, TaxPlanBreakdown } from '../../utils/taxPlan';
import type { ComputedBonusPlan } from '../../utils/bonusPlan';
import { formatScenarioMoney } from '../../utils/cashFlowScenario';
import ProfitShareBar from './ProfitShareBar';
import ProfitPlannerPanel, { type CockpitProfitInputs } from './ProfitPlannerPanel';
import BonusPlannerModal from './BonusPlannerModal';
import TaxPlannerModal from './TaxPlannerModal';
import {
  analyzeCashFloor,
  analyzeReasonableComp,
  cashFloorBreachCopy,
  cashFloorHeadroomLabel,
  formatMonthKeyLabel,
  distributionsYtd,
  expandDebtSchedule,
  formatPct,
  resolveOwnerPositionIds,
} from '../../utils/cockpitCalcs';
import { useCockpitWorkspace } from '../../hooks/useCockpitWorkspace';
import BalanceSheetCard from './BalanceSheetCard';
import type { BillPayClearingSettings } from '../../utils/billPayClearing';
import { DEFAULT_BILL_PAY_CLEARING } from '../../utils/billPayClearing';

type CockpitTab =
  | 'overview'
  | 'profit'
  | 'floor'
  | 'compensation'
  | 'distributions'
  | 'debt'
  | 'bonuses'
  | 'tax';

const TABS: Array<{ id: CockpitTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'floor', label: 'Cash floor' },
  { id: 'compensation', label: 'Compensation' },
  { id: 'distributions', label: 'Distributions' },
  { id: 'debt', label: 'Debt' },
];

export interface CockpitObligationPanel {
  year: number;
  years: number[];
  onYearChange: (year: number) => void;
  bonusPlan: BonusPlan;
  onBonusChange: (plan: BonusPlan) => void;
  bonusComputed: ComputedBonusPlan;
  taxPlan: TaxPlan;
  onTaxChange: (plan: TaxPlan) => void;
  taxSettings: ObligationSettings;
  onTaxSettingsChange: (settings: ObligationSettings) => void;
  taxComputed: ComputedTaxPlan;
  estimatedProfit: number;
  onProfitChange: (profit: number) => void;
  profitHint: number | null;
  baselineLowest: number | null;
  draftLowest: number | null;
  busy: boolean;
  error: string | null;
  onSaveBonus: () => void;
  onSaveTax: () => void;
}

interface CockpitModalProps {
  onClose: () => void;
  projection: CashFlowProjection | null;
  dailyReport: DailyFinanceReport | null;
  overhead: OverheadExpense[];
  positions: PayrollPosition[];
  taxBreakdown: TaxPlanBreakdown | null;
  bonusComputed?: ComputedBonusPlan | null;
  actorUserId?: string;
  canWrite?: boolean;
  canViewObligations?: boolean;
  obligationPanel?: CockpitObligationPanel | null;
  profitInputs?: CockpitProfitInputs | null;
  horizon: number;
  /** AP bank-clearing lag after Mark paid (Forecast + financeAgentApi). */
  billPayClearing?: BillPayClearingSettings;
  onBillPayClearingChange?: (next: BillPayClearingSettings) => void;
  onSaveBillPayClearing?: () => void;
}

function toInputDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fromInputDate(s: string, fallback: Date): Date {
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return fallback;
  return new Date(y, m - 1, d);
}

const FLAG_COPY: Record<ReturnType<typeof analyzeReasonableComp>['flag'], string> = {
  ok: 'W-2 vs K-1 split looks usable as a planning snapshot for the CPA.',
  low_w2: 'Owner W-2 is under 25% of W-2 + K-1. The CPA will likely want a reasonable-compensation discussion.',
  no_k1: 'No K-1 remainder in the tax planner yet — enter estimated profit there.',
  no_w2: 'K-1 is positive but no owner W-2. S-Corp rules expect reasonable salary.',
};

function cashFloorCompareSub(analysis: ReturnType<typeof analyzeCashFloor>): string {
  if (analysis.lowestBalance == null) return 'Need the cash projection';
  const month = formatMonthKeyLabel(analysis.lowestMonthKey);
  const low = formatScenarioMoney(analysis.lowestBalance);
  const floorAmt = formatScenarioMoney(analysis.minimumCash);
  const lowBit = month ? `${month} low ${low}` : `Projected low ${low}`;
  if (analysis.minimumCash <= 0) return `${lowBit} · set a never-below floor`;
  if (analysis.belowFloor) {
    const breach = cashFloorBreachCopy(analysis);
    return `${breach ?? 'Below the cash floor'} · ${lowBit} vs ${floorAmt} floor`;
  }
  return `${lowBit} − ${floorAmt} floor`;
}

const CockpitModal: React.FC<CockpitModalProps> = ({
  onClose,
  projection,
  dailyReport,
  overhead,
  positions,
  taxBreakdown,
  bonusComputed = null,
  actorUserId,
  canWrite = true,
  canViewObligations = false,
  obligationPanel = null,
  profitInputs = null,
  horizon,
  billPayClearing = DEFAULT_BILL_PAY_CLEARING,
  onBillPayClearingChange,
  onSaveBillPayClearing,
}) => {
  const cockpit = useCockpitWorkspace(actorUserId);
  const [tab, setTab] = useState<CockpitTab>('overview');
  const [floorDraft, setFloorDraft] = useState<number | null>(null);
  const [clearingBusy, setClearingBusy] = useState(false);
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const [ownersDraft, setOwnersDraft] = useState<string[] | null>(null);
  const [drawAmount, setDrawAmount] = useState('');
  const [drawKind, setDrawKind] = useState<OwnerDrawKind>('tax_cover');
  const [drawDate, setDrawDate] = useState(() => toInputDate(new Date()));
  const [drawNote, setDrawNote] = useState('');
  const [debtDraft, setDebtDraft] = useState({
    name: '',
    lender: '',
    principalRemaining: '',
    annualRatePct: '',
    paymentAmount: '',
    paymentDay: '1',
    remainingPayments: '',
    balloonAmount: '',
    balloonDate: '',
    alreadyInBurn: true,
  });

  const settings: CockpitSettings = {
    ...cockpit.settings,
    minimumCash: floorDraft ?? cockpit.settings.minimumCash,
    compensationNotes: notesDraft ?? cockpit.settings.compensationNotes,
    ownerPositionIds: ownersDraft ?? cockpit.settings.ownerPositionIds,
  };

  const ownerIds = useMemo(
    () => resolveOwnerPositionIds(positions, settings.ownerPositionIds),
    [positions, settings.ownerPositionIds],
  );
  const ownerIdSet = useMemo(() => new Set(ownerIds), [ownerIds]);
  const ownerCandidates = useMemo(
    () => positions.filter((p) => p.isActive).slice().sort((a, b) => a.title.localeCompare(b.title)),
    [positions],
  );

  const floor = useMemo(
    () => analyzeCashFloor(projection, settings.minimumCash),
    [projection, settings.minimumCash],
  );
  const comp = useMemo(
    () => analyzeReasonableComp(positions, taxBreakdown, settings.ownerPositionIds),
    [positions, settings.ownerPositionIds, taxBreakdown],
  );
  const ytd = useMemo(
    () => distributionsYtd(cockpit.draws, new Date().getFullYear()),
    [cockpit.draws],
  );
  const debtSlices = useMemo(
    () => expandDebtSchedule(cockpit.debts, horizon),
    [cockpit.debts, horizon],
  );
  const loanOverhead = overhead.filter((e) => e.isActive && e.category === 'loans');
  const pods = dailyReport?.sequencePods ?? [];

  const saveSettings = () => {
    void cockpit.persistSettings({
      minimumCash: Math.max(0, Number(settings.minimumCash) || 0),
      compensationNotes: settings.compensationNotes,
      ownerPositionIds: settings.ownerPositionIds ?? null,
    });
    setFloorDraft(null);
    setNotesDraft(null);
    setOwnersDraft(null);
  };

  const toggleOwner = (positionId: string, checked: boolean) => {
    const next = new Set(ownerIds);
    if (checked) next.add(positionId);
    else next.delete(positionId);
    setOwnersDraft(Array.from(next));
  };

  return (
    <div className="fin-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="fin-modal fin-modal--planning fin-modal--cockpit"
        role="dialog"
        aria-labelledby="cockpit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fin-modal-header">
          <div>
            <h3 id="cockpit-title">
              <LayoutDashboard size={16} aria-hidden /> Cockpit
            </h3>
            <p className="fin-planning-kicker">
              Cash floor, books, and owner pay — planning numbers, not the CPA’s books.
            </p>
          </div>
          <div className="fin-cockpit-header-actions">
            <button type="button" className="fin-modal-close" onClick={onClose} aria-label="Close cockpit">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="fin-cockpit-body">
          <nav className="fin-cockpit-rail" aria-label="Cockpit sections">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`fin-cockpit-rail-item${tab === item.id ? ' is-active' : ''}`}
                onClick={() => setTab(item.id)}
              >
                {item.label}
              </button>
            ))}
            {canViewObligations && (
              <>
                <div className="fin-cockpit-rail-divider" role="presentation" />
                <button
                  type="button"
                  className={`fin-cockpit-rail-item${tab === 'profit' ? ' is-active' : ''}`}
                  onClick={() => setTab('profit')}
                >
                  Profit
                </button>
                <button
                  type="button"
                  className={`fin-cockpit-rail-item${tab === 'bonuses' ? ' is-active' : ''}`}
                  onClick={() => setTab('bonuses')}
                >
                  Bonuses
                </button>
                <button
                  type="button"
                  className={`fin-cockpit-rail-item${tab === 'tax' ? ' is-active' : ''}`}
                  onClick={() => setTab('tax')}
                >
                  Tax
                </button>
              </>
            )}
          </nav>

          <div className="fin-cockpit-panel">
            {cockpit.error && <div className="fin-modal-error">{cockpit.error}</div>}

            {tab === 'overview' && (
              <>
                <div className="fin-obl-kpis">
                  <div className="fin-kpi-card">
                    <span className="fin-kpi-label">Sequence cash</span>
                    <span className="fin-kpi-value">{formatScenarioMoney(dailyReport?.sequencePodAmount ?? 0)}</span>
                    <span className="fin-kpi-sub">{dailyReport?.dateKey || 'No pod snapshot yet'}</span>
                  </div>
                  <div className="fin-kpi-card">
                    <span className="fin-kpi-label">Cash floor</span>
                    <span className="fin-kpi-value">{formatScenarioMoney(floor.minimumCash)}</span>
                    <span className={`fin-kpi-sub${floor.belowFloor ? ' fin-cockpit-floor-warn' : ''}`}>
                      {floor.minimumCash <= 0
                        ? 'Set a never-below amount in Cash floor'
                        : cashFloorCompareSub(floor)}
                    </span>
                  </div>
                  <div className="fin-kpi-card">
                    <span className="fin-kpi-label">Lowest projected</span>
                    <span className="fin-kpi-value">{formatScenarioMoney(floor.lowestBalance ?? 0)}</span>
                    <span className="fin-kpi-sub">{formatMonthKeyLabel(floor.lowestMonthKey) || 'Need the cash projection'}</span>
                  </div>
                  <div className="fin-kpi-card">
                    <span className="fin-kpi-label">Year-end profit</span>
                    {canViewObligations ? (
                      <button
                        type="button"
                        className="fin-kpi-value fin-kpi-link"
                        onClick={() => setTab('profit')}
                      >
                        {formatScenarioMoney(taxBreakdown?.estimatedProfit ?? 0)}
                      </button>
                    ) : (
                      <span className="fin-kpi-value">{formatScenarioMoney(taxBreakdown?.estimatedProfit ?? 0)}</span>
                    )}
                    <span className="fin-kpi-sub">
                      {canViewObligations && bonusComputed
                        ? `${bonusComputed.bonusPool ? 'Bonus pool ' + formatScenarioMoney(bonusComputed.bonusPool) : 'Open Profit to set the target'}`
                        : 'From the tax planner'}
                    </span>
                  </div>
                </div>
                {canViewObligations && (taxBreakdown?.estimatedProfit || bonusComputed?.bonusPool) ? (
                  <ProfitShareBar
                    profit={taxBreakdown?.estimatedProfit ?? bonusComputed?.estimatedProfit ?? 0}
                    bonusPool={bonusComputed?.bonusPool ?? 0}
                    employerTax={bonusComputed?.employerTaxTotal ?? 0}
                    emptyHint="Enter year-end profit in Profit to see the stack."
                    taxCash={taxBreakdown?.companyCashTotal ?? 0}
                  />
                ) : null}
                {pods.length > 0 && (
                  <div className="fin-forecast-table-scroll">
                    <table className="fin-forecast-table" aria-label="Sequence pods">
                      <thead>
                        <tr>
                          <th>Pod</th>
                          <th className="right">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pods.map((pod) => (
                          <tr key={pod.id}>
                            <td>{pod.name}</td>
                            <td className="right">{formatScenarioMoney(pod.balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <BalanceSheetCard />
              </>
            )}

            {tab === 'floor' && (
              <>
                <p className="fin-modal-help">
                  A never-below cash rule against the live forecast. The gap is the lowest projected
                  month minus this floor — not Sequence cash today. Months under the floor show here;
                  they do not change burn or CapEx.
                </p>
                <div className="fin-obl-grid">
                  <label className="fin-modal-field">
                    <span className="fin-modal-label">Minimum cash</span>
                    <input
                      className="fin-modal-input"
                      type="number"
                      min={0}
                      step={1000}
                      value={settings.minimumCash || ''}
                      onChange={(e) => setFloorDraft(Number(e.target.value) || 0)}
                      disabled={!canWrite}
                    />
                  </label>
                </div>
                <div className="fin-obl-kpis">
                  <div className="fin-kpi-card">
                    <span className="fin-kpi-label">{cashFloorHeadroomLabel(floor)}</span>
                    <span
                      className={`fin-kpi-value${floor.belowFloor ? ' fin-planning-danger' : ''}`}
                    >
                      {formatScenarioMoney(floor.headroom ?? 0)}
                    </span>
                    <span className={`fin-kpi-sub${floor.belowFloor ? ' fin-cockpit-floor-warn' : ''}`}>
                      {cashFloorCompareSub(floor)}
                    </span>
                  </div>
                  <div className="fin-kpi-card">
                    <span className="fin-kpi-label">Months below</span>
                    <span className="fin-kpi-value">{floor.monthsBelow}</span>
                    <span className="fin-kpi-sub">In the current forecast window</span>
                  </div>
                </div>
                {canWrite && (
                  <button type="button" className="fin-primary-btn" onClick={saveSettings} disabled={cockpit.busy}>
                    Save floor
                  </button>
                )}

                <h3 className="fin-modal-section-title" style={{ marginTop: 24 }}>Bill pay clearing lag</h3>
                <p className="fin-modal-help">
                  After Billing &amp; Payables marks a bill paid, Forecast keeps it as clearing AP
                  until QuickBooks shows the bank clear — or until this lag elapses. Defaults:
                  ACH 2 business days, check 7, card on the statement day.
                </p>
                <div className="fin-obl-grid">
                  <label className="fin-modal-field">
                    <span className="fin-modal-label">ACH business days</span>
                    <input
                      className="fin-modal-input"
                      type="number"
                      min={0}
                      max={30}
                      value={billPayClearing.achBusinessDays}
                      onChange={(e) =>
                        onBillPayClearingChange?.({
                          ...billPayClearing,
                          achBusinessDays: Math.max(0, Math.min(30, Number(e.target.value) || 0)),
                        })
                      }
                      disabled={!canWrite || !onBillPayClearingChange}
                    />
                  </label>
                  <label className="fin-modal-field">
                    <span className="fin-modal-label">Check business days</span>
                    <input
                      className="fin-modal-input"
                      type="number"
                      min={0}
                      max={45}
                      value={billPayClearing.checkBusinessDays}
                      onChange={(e) =>
                        onBillPayClearingChange?.({
                          ...billPayClearing,
                          checkBusinessDays: Math.max(0, Math.min(45, Number(e.target.value) || 0)),
                        })
                      }
                      disabled={!canWrite || !onBillPayClearingChange}
                    />
                  </label>
                  <label className="fin-modal-field">
                    <span className="fin-modal-label">Card due day of month (1–28, blank = 25th next month)</span>
                    <input
                      className="fin-modal-input"
                      type="number"
                      min={1}
                      max={28}
                      value={billPayClearing.cardDueDayOfMonth ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        const n = Number(raw);
                        onBillPayClearingChange?.({
                          ...billPayClearing,
                          cardDueDayOfMonth:
                            raw === '' || !Number.isFinite(n)
                              ? null
                              : Math.max(1, Math.min(28, Math.floor(n))),
                        });
                      }}
                      disabled={!canWrite || !onBillPayClearingChange}
                    />
                  </label>
                </div>
                {canWrite && onSaveBillPayClearing && (
                  <button
                    type="button"
                    className="fin-primary-btn"
                    disabled={clearingBusy}
                    onClick={() => {
                      setClearingBusy(true);
                      void Promise.resolve(onSaveBillPayClearing()).finally(() => setClearingBusy(false));
                    }}
                  >
                    Save clearing lag
                  </button>
                )}
              </>
            )}

            {tab === 'compensation' && (
              <>
                <p className="fin-modal-help">
                  Planning snapshot for the CPA: owner W-2 already in payroll versus K-1
                  remainder from the tax planner. Not a reasonable-compensation opinion.
                  Check the people who actually have an ownership stake — leadership titles
                  are only a starting guess.
                </p>
                <div className="fin-obl-kpis">
                  <div className="fin-kpi-card">
                    <span className="fin-kpi-label">Owner W-2</span>
                    <span className="fin-kpi-value">{formatScenarioMoney(comp.ownerW2)}</span>
                    <span className="fin-kpi-sub">{comp.ownerTitles.join(', ') || 'No owners selected'}</span>
                  </div>
                  <div className="fin-kpi-card">
                    <span className="fin-kpi-label">K-1 remainder</span>
                    <span className="fin-kpi-value">{formatScenarioMoney(comp.k1Remainder)}</span>
                    <span className="fin-kpi-sub">Net profit after the bonus plan</span>
                  </div>
                  <div className="fin-kpi-card">
                    <span className="fin-kpi-label">W-2 share of owner stack</span>
                    <span className="fin-kpi-value">{formatPct(comp.w2ShareOfOwner)}</span>
                    <span className="fin-kpi-sub">All-company W-2 {formatScenarioMoney(comp.allW2)}</span>
                  </div>
                </div>
                <p className="fin-modal-help">{FLAG_COPY[comp.flag]}</p>
                <div className="fin-forecast-table-scroll">
                  <table className="fin-forecast-table" aria-label="People with ownership">
                    <thead>
                      <tr>
                        <th className="fin-owner-check-col">Owner</th>
                        <th>Position</th>
                        <th>Department</th>
                        <th className="right">W-2</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ownerCandidates.length === 0 && (
                        <tr><td colSpan={4}>No active payroll positions.</td></tr>
                      )}
                      {ownerCandidates.map((p) => (
                        <tr key={p.id}>
                          <td className="fin-owner-check-col">
                            <input
                              type="checkbox"
                              checked={ownerIdSet.has(p.id)}
                              onChange={(e) => toggleOwner(p.id, e.target.checked)}
                              disabled={!canWrite}
                              aria-label={`${p.title} has ownership`}
                            />
                          </td>
                          <td>{p.title || 'Untitled'}</td>
                          <td>{PAYROLL_DEPARTMENT_LABELS[p.department] || p.department}</td>
                          <td className="right">{formatScenarioMoney(p.annualSalary)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <label className="fin-modal-field full">
                  <span className="fin-modal-label">Memo for the CPA</span>
                  <textarea
                    className="fin-modal-textarea"
                    rows={4}
                    value={settings.compensationNotes}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    disabled={!canWrite}
                    placeholder="Role, hours, why this salary is reasonable…"
                  />
                </label>
                {canWrite && (
                  <button type="button" className="fin-primary-btn" onClick={saveSettings} disabled={cockpit.busy}>
                    Save compensation
                  </button>
                )}
              </>
            )}

            {tab === 'distributions' && (
              <>
                <p className="fin-modal-help">
                  Owner draws this year. Use “Cover owner tax” for 1040-ES distributions so the
                  packet matches the tax planner.
                </p>
                <div className="fin-obl-kpis">
                  <div className="fin-kpi-card">
                    <span className="fin-kpi-label">YTD distributions</span>
                    <span className="fin-kpi-value">{formatScenarioMoney(ytd.total)}</span>
                    <span className="fin-kpi-sub">
                      Tax {formatScenarioMoney(ytd.taxCover)} · profit {formatScenarioMoney(ytd.profit)}
                    </span>
                  </div>
                </div>
                {canWrite && (
                  <div className="fin-obl-grid">
                    <label className="fin-modal-field">
                      <span className="fin-modal-label">Date</span>
                      <input className="fin-modal-input" type="date" value={drawDate} onChange={(e) => setDrawDate(e.target.value)} />
                    </label>
                    <label className="fin-modal-field">
                      <span className="fin-modal-label">Amount</span>
                      <input className="fin-modal-input" type="number" min={0} value={drawAmount} onChange={(e) => setDrawAmount(e.target.value)} />
                    </label>
                    <label className="fin-modal-field">
                      <span className="fin-modal-label">Kind</span>
                      <select className="fin-modal-select" value={drawKind} onChange={(e) => setDrawKind(e.target.value as OwnerDrawKind)}>
                        {OWNER_DRAW_KINDS.map((k) => (
                          <option key={k} value={k}>{OWNER_DRAW_KIND_LABELS[k]}</option>
                        ))}
                      </select>
                    </label>
                    <label className="fin-modal-field">
                      <span className="fin-modal-label">Note</span>
                      <input className="fin-modal-input" value={drawNote} onChange={(e) => setDrawNote(e.target.value)} />
                    </label>
                    <button
                      type="button"
                      className="fin-primary-btn"
                      disabled={cockpit.busy || !Number(drawAmount)}
                      onClick={() => {
                        void cockpit.addDraw({
                          date: fromInputDate(drawDate, new Date()),
                          amount: Math.round(Number(drawAmount) || 0),
                          kind: drawKind,
                          note: drawNote.trim(),
                        }).then(() => {
                          setDrawAmount('');
                          setDrawNote('');
                        });
                      }}
                    >
                      Add draw
                    </button>
                  </div>
                )}
                <div className="fin-forecast-table-scroll">
                  <table className="fin-forecast-table" aria-label="Owner distributions">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Kind</th>
                        <th>Note</th>
                        <th className="right">Amount</th>
                        {canWrite && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {cockpit.draws.length === 0 && (
                        <tr><td colSpan={canWrite ? 5 : 4}>No distributions logged.</td></tr>
                      )}
                      {cockpit.draws.map((d) => (
                        <tr key={d.id}>
                          <td>{d.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                          <td>{OWNER_DRAW_KIND_LABELS[d.kind]}</td>
                          <td>{d.note || '—'}</td>
                          <td className="right">{formatScenarioMoney(d.amount)}</td>
                          {canWrite && (
                            <td>
                              <button type="button" className="fin-secondary-btn" onClick={() => void cockpit.removeDraw(d.id)}>
                                Remove
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {tab === 'debt' && (
              <>
                <p className="fin-modal-help">
                  Payment calendar for loans and equipment notes. Mark “already in burn” when the
                  payment is already an overhead loan row so this stays a schedule, not a second cash-out.
                </p>
                {loanOverhead.length > 0 && (
                  <p className="fin-modal-help">
                    Overhead already has {loanOverhead.length} loan line{loanOverhead.length === 1 ? '' : 's'}
                    {' '}({formatScenarioMoney(loanOverhead.reduce((s, e) => s + e.amount, 0))} listed).
                  </p>
                )}
                {canWrite && (
                  <div className="fin-obl-grid">
                    <label className="fin-modal-field">
                      <span className="fin-modal-label">Name</span>
                      <input className="fin-modal-input" value={debtDraft.name} onChange={(e) => setDebtDraft({ ...debtDraft, name: e.target.value })} />
                    </label>
                    <label className="fin-modal-field">
                      <span className="fin-modal-label">Lender</span>
                      <input className="fin-modal-input" value={debtDraft.lender} onChange={(e) => setDebtDraft({ ...debtDraft, lender: e.target.value })} />
                    </label>
                    <label className="fin-modal-field">
                      <span className="fin-modal-label">Principal left</span>
                      <input className="fin-modal-input" type="number" value={debtDraft.principalRemaining} onChange={(e) => setDebtDraft({ ...debtDraft, principalRemaining: e.target.value })} />
                    </label>
                    <label className="fin-modal-field">
                      <span className="fin-modal-label">Rate %</span>
                      <input className="fin-modal-input" type="number" value={debtDraft.annualRatePct} onChange={(e) => setDebtDraft({ ...debtDraft, annualRatePct: e.target.value })} />
                    </label>
                    <label className="fin-modal-field">
                      <span className="fin-modal-label">Payment $</span>
                      <input className="fin-modal-input" type="number" value={debtDraft.paymentAmount} onChange={(e) => setDebtDraft({ ...debtDraft, paymentAmount: e.target.value })} />
                    </label>
                    <label className="fin-modal-field">
                      <span className="fin-modal-label">Day of month</span>
                      <input className="fin-modal-input" type="number" min={1} max={28} value={debtDraft.paymentDay} onChange={(e) => setDebtDraft({ ...debtDraft, paymentDay: e.target.value })} />
                    </label>
                    <label className="fin-modal-field">
                      <span className="fin-modal-label">Remaining payments</span>
                      <input className="fin-modal-input" type="number" placeholder="open" value={debtDraft.remainingPayments} onChange={(e) => setDebtDraft({ ...debtDraft, remainingPayments: e.target.value })} />
                    </label>
                    <label className="fin-modal-field">
                      <span className="fin-modal-label">Balloon $</span>
                      <input className="fin-modal-input" type="number" value={debtDraft.balloonAmount} onChange={(e) => setDebtDraft({ ...debtDraft, balloonAmount: e.target.value })} />
                    </label>
                    <label className="fin-modal-field">
                      <span className="fin-modal-label">Balloon date</span>
                      <input className="fin-modal-input" type="date" value={debtDraft.balloonDate} onChange={(e) => setDebtDraft({ ...debtDraft, balloonDate: e.target.value })} />
                    </label>
                    <label className="fin-modal-toggle">
                      <input
                        type="checkbox"
                        checked={debtDraft.alreadyInBurn}
                        onChange={(e) => setDebtDraft({ ...debtDraft, alreadyInBurn: e.target.checked })}
                      />
                      Already in overhead burn
                    </label>
                    <button
                      type="button"
                      className="fin-primary-btn"
                      disabled={cockpit.busy || !debtDraft.name}
                      onClick={() => {
                        void cockpit.addDebt({
                          name: debtDraft.name.trim(),
                          lender: debtDraft.lender.trim(),
                          principalRemaining: Number(debtDraft.principalRemaining) || 0,
                          annualRatePct: Number(debtDraft.annualRatePct) || 0,
                          paymentAmount: Number(debtDraft.paymentAmount) || 0,
                          paymentDay: Number(debtDraft.paymentDay) || 1,
                          remainingPayments: debtDraft.remainingPayments === '' ? null : Number(debtDraft.remainingPayments),
                          balloonAmount: Number(debtDraft.balloonAmount) || 0,
                          balloonDate: debtDraft.balloonDate ? fromInputDate(debtDraft.balloonDate, new Date()) : null,
                          notes: '',
                          isActive: true,
                          alreadyInBurn: debtDraft.alreadyInBurn,
                        }).then(() => {
                          setDebtDraft({
                            name: '',
                            lender: '',
                            principalRemaining: '',
                            annualRatePct: '',
                            paymentAmount: '',
                            paymentDay: '1',
                            remainingPayments: '',
                            balloonAmount: '',
                            balloonDate: '',
                            alreadyInBurn: true,
                          });
                        });
                      }}
                    >
                      Add loan
                    </button>
                  </div>
                )}
                <div className="fin-forecast-table-scroll">
                  <table className="fin-forecast-table" aria-label="Debt instruments">
                    <thead>
                      <tr>
                        <th>Loan</th>
                        <th className="right">Principal</th>
                        <th className="right">Payment</th>
                        <th>In burn</th>
                        {canWrite && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {cockpit.debts.length === 0 && (
                        <tr><td colSpan={canWrite ? 5 : 4}>No loans on the schedule yet.</td></tr>
                      )}
                      {cockpit.debts.map((d) => (
                        <tr key={d.id}>
                          <td>{d.name}{d.lender ? ` · ${d.lender}` : ''}</td>
                          <td className="right">{formatScenarioMoney(d.principalRemaining)}</td>
                          <td className="right">{formatScenarioMoney(d.paymentAmount)}</td>
                          <td>{d.alreadyInBurn ? 'Yes' : 'No'}</td>
                          {canWrite && (
                            <td>
                              <button type="button" className="fin-secondary-btn" onClick={() => void cockpit.removeDebt(d.id)}>
                                Remove
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {debtSlices.length > 0 && (
                  <div className="fin-forecast-table-scroll">
                    <table className="fin-forecast-table" aria-label="Upcoming debt payments">
                      <thead>
                        <tr>
                          <th>Due</th>
                          <th>Item</th>
                          <th className="right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {debtSlices.slice(0, 18).map((s, idx) => (
                          <tr key={`${s.instrumentId}-${idx}`}>
                            <td>{s.dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                            <td>{s.name}{s.kind === 'balloon' ? ' (balloon)' : ''}</td>
                            <td className="right">{formatScenarioMoney(s.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {tab === 'profit' && obligationPanel && (
              <ProfitPlannerPanel
                year={obligationPanel.year}
                years={obligationPanel.years}
                onYearChange={obligationPanel.onYearChange}
                estimatedProfit={obligationPanel.estimatedProfit}
                onProfitChange={obligationPanel.onProfitChange}
                profitHint={obligationPanel.profitHint}
                taxBreakdown={taxBreakdown ?? obligationPanel.taxComputed.breakdown}
                bonusComputed={bonusComputed ?? obligationPanel.bonusComputed}
                baselineLowest={obligationPanel.baselineLowest}
                draftLowest={obligationPanel.draftLowest}
                busy={obligationPanel.busy}
                error={obligationPanel.error}
                onSave={obligationPanel.onSaveTax}
                inputs={profitInputs ?? {
                  moneyInRows: [],
                  buckets: [],
                  deals: [],
                  salesOrders: [],
                  changeOrders: [],
                }}
              />
            )}

            {tab === 'bonuses' && obligationPanel && (
              <BonusPlannerModal
                year={obligationPanel.year}
                years={obligationPanel.years}
                onYearChange={obligationPanel.onYearChange}
                plan={obligationPanel.bonusPlan}
                onChange={obligationPanel.onBonusChange}
                computed={obligationPanel.bonusComputed}
                positions={positions}
                estimatedProfit={obligationPanel.estimatedProfit}
                onProfitChange={obligationPanel.onProfitChange}
                profitHint={obligationPanel.profitHint}
                baselineLowest={obligationPanel.baselineLowest}
                draftLowest={obligationPanel.draftLowest}
                busy={obligationPanel.busy}
                error={obligationPanel.error}
                onSave={obligationPanel.onSaveBonus}
              />
            )}

            {tab === 'tax' && obligationPanel && (
              <TaxPlannerModal
                year={obligationPanel.year}
                years={obligationPanel.years}
                onYearChange={obligationPanel.onYearChange}
                plan={obligationPanel.taxPlan}
                settings={obligationPanel.taxSettings}
                onChange={obligationPanel.onTaxChange}
                onSettingsChange={obligationPanel.onTaxSettingsChange}
                computed={obligationPanel.taxComputed}
                profitHint={obligationPanel.profitHint}
                baselineLowest={obligationPanel.baselineLowest}
                draftLowest={obligationPanel.draftLowest}
                busy={obligationPanel.busy}
                error={obligationPanel.error}
                onSave={obligationPanel.onSaveTax}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CockpitModal;
